import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import merge from "lodash.merge";
import { Loader2, PlusCircle } from "lucide-react";
import type { RequestData } from "server/src/trpc/routers/request/requestUtils";
import {
  cardDefaults,
  createIndividualCardClient as cardValidator,
  refreshIntervalOptions,
} from "shared";
import { toast } from "sonner";
import type z from "zod";
import { AddressInputField } from "@/components/fields/address-input-field";
import { AdvancedFields } from "@/components/fields/advanced-fields";
import {
  CardTypeField,
  cardTypeFieldMapping,
} from "@/components/fields/card-type-field";
import { LimitFlexibilityInputField } from "@/components/fields/limit-flexibility-input";
import { Button } from "@/components/ui/button";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import { useAppForm } from "@/hooks/useCreateForm";
import { restrictRoute } from "@/lib/routeBlocker";

export const Route = createFileRoute("/_authed/_org/requests/$id/edit")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/requests",
      },
      requiredRole: "admin",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { id } = Route.useParams();

  const { data: request } = useSuspenseQuery(
    trpc.request.get.queryOptions({
      requestId: id,
    }),
  );

  if (request.status !== "pending" || request.type !== "card")
    navigate({
      params: { id },
      to: "/requests/$id",
    });

  const { mutate: edit, isPending } = useMutation(
    trpc.request.edit.mutationOptions({
      onError: (error) => {
        toast.error("Error", {
          description: error.message,
          position: "bottom-left",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          trpc.request.get.queryOptions({ requestId: request.id }),
        );
        queryClient.invalidateQueries(trpc.request.list.queryOptions());

        toast.success("Request Updated", {
          position: "bottom-left",
        });

        navigate({ to: "/requests" });
      },
    }),
  );

  const form = useAppForm({
    canSubmitWhenInvalid: true,
    defaultValues: merge(cardDefaults, request.data as RequestData) as z.infer<
      typeof cardValidator
    >,
    onSubmit: ({ value }) => {
      const { addressId: _addressId, address, ...rest } = value;
      edit({
        ...rest,
        address: value.type === "physical" ? address : undefined,
        requestId: request.id,
      });
    },
    onSubmitInvalid: ({ formApi }) => {
      console.log(formApi.getAllErrors());
      toast.error("Error", {
        description: "Fill in all required fields",
        position: "bottom-left",
      });
    },
    validators: {
      onSubmit: cardValidator,
    },
  });

  const { data: approvalPolicies } = useSuspenseQuery(
    trpc.approvalPolicy.list.queryOptions(),
  );

  return (
    <form
      noValidate
      className="flex min-h-0 flex-1 flex-col border-t"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {/* Scrollable content area with fixed max height */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-4">
        <FormStepSeparator description="Details" />
        <div className="mb-2 space-y-4">
          <form.Subscribe
            selector={(state) => state.values}
            children={(_state) => <div />}
          />

          <CardTypeField form={form} fields={cardTypeFieldMapping} />

          <form.AppField
            children={(field) => (
              <field.TextField label="Card Nickname" required={true} />
            )}
            name="name"
          />
        </div>

        {approvalPolicies.length > 0 && (
          <FormStepSeparator description="Policies" />
        )}

        <div className="mb-4 flex flex-col gap-4">
          <form.AppField
            children={(field) => (
              <field.ExpensePolicySelectField
                label="Spend Policy"
                mode="create"
              />
            )}
            name="policyId"
          />

          {approvalPolicies.length > 0 && (
            <form.AppField
              children={(field) => (
                <field.ApprovalPolicySelectField
                  label="Approval Policy"
                  mode="create"
                />
              )}
              name="approvalPolicyId"
            />
          )}
        </div>

        <form.Subscribe
          selector={(state) => state.values.type}
          children={(type) =>
            type === "physical" && (
              <div className="my-4">
                <FormStepSeparator description="Shipping" />

                <form.AppField
                  children={(field) => (
                    <field.AddressField
                      handleAddressSelect={(address) =>
                        form.setFieldValue("address", address)
                      }
                      label="Shipping Address"
                      required={true}
                    />
                  )}
                  name="addressId"
                />

                <form.Subscribe
                  selector={(state) => state.values.addressId}
                  children={(addressId) =>
                    addressId === "custom_address" && (
                      <AddressInputField
                        form={form}
                        renderStyle="compact"
                        fields={{
                          address: "address",
                          saveNewAddress: "saveNewAddress",
                        }}
                      />
                    )
                  }
                />
              </div>
            )
          }
        />

        <FormStepSeparator description="Control Spend" />
        <div className="space-y-4">
          <LimitFlexibilityInputField
            form={form}
            hideLeniencyInput={false}
            fields={{
              restrictions: "restrictions",
            }}
          />
          <form.AppField
            children={(field) => {
              return (
                <field.SelectField
                  label="Frequency"
                  options={refreshIntervalOptions}
                  required={true}
                />
              );
            }}
            name="restrictions.refreshInterval"
          />

          <AdvancedFields>
            <form.AppField
              // @ts-ignore Array types not being inferred correctly
              name="restrictions.allowedCategories"
              children={(field) => {
                return (
                  <field.SpendCategorySelect
                    label="Allowed Spend Categories"
                    required={false}
                  />
                );
              }}
            />
            <form.AppField
              name="restrictions.schedule.activateAt"
              children={(field) => (
                <field.DateTimeField
                  label="Start Date"
                  descriptionStyle="below"
                  description="Card will be locked until this date"
                  range={{
                    before: new Date(),
                  }}
                />
              )}
            />

            <form.Subscribe
              selector={(state) =>
                state.values.restrictions.schedule?.activateAt
              }
              children={(activateAt) => (
                <form.AppField
                  name="restrictions.schedule.lockAt"
                  children={(field) => (
                    <field.DateTimeField
                      label="Lock Date"
                      descriptionStyle="below"
                      description="Card will automatically lock on this date"
                      range={{
                        before: activateAt ?? new Date(),
                      }}
                    />
                  )}
                />
              )}
            />
          </AdvancedFields>
        </div>
      </div>
      <div className="flex min-h-16 w-full flex-row items-center justify-between border-t p-6">
        <Button
          className="font-semibold"
          // onClick={() => form.handleSubmit()}
          disabled={isPending}
          size="default"
          type="submit"
        >
          {isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <PlusCircle className="mr-2 h-4 w-4" />
          )}
          Edit Request
        </Button>
      </div>
    </form>
  );
}
